import React, { useState, useEffect } from "react";
import { HiOutlineSpeakerWave } from "react-icons/hi2";
import "./FlashcardItem.css";
import { IoFlagOutline } from "react-icons/io5";
import { useAuth } from "../../context/AuthContext";
import { previewNextLabel, scheduleNext } from "../../utils/flashcardalgorithm";
import { submitFlashcardReport } from "../../data/flashcardsRepo";

import ReportPopup from "./ReportPopup";


export default function FlashcardItem({ card, onStatusChange,  trainingMode = false }) {
  const [showTranslation, setShowTranslation] = useState(false);
  const [animateBorder, setAnimateBorder] = useState(null);
  const [uiStatus, setUiStatus] = useState(card.status);
  const [showReport, setShowReport] = useState(false);
  const { user } = useAuth();


   // keep uiStatus in sync whenever card/status changes (e.g., next card)
   useEffect(() => {
    setUiStatus(card.status);
  }, [card.id, card.status]); 


  if (!card) return null;

  const {
    word,
    article,
    audio,
    sentence,
    sentenceTranslation,
    translations_pt,
    plural,
    pos,
  } = card;

  const playSound = (file) => {
    const audio = new Audio(file); // ✅ leading slash = from /public
    audio.volume = 0.1;
    audio.play();
  };

  const handleReportSubmit = async (data) => {
    try {
      if (!user?.uid) throw new Error("Not authenticated");
  
      await submitFlashcardReport({
        uid: user.uid,
        flashcard: card,
        reason: data.reason,
        details: data.details,
      });
  
      console.log("✅ Report saved!");
    } catch (err) {
      console.error("Error saving report:", err);
    }
  };
  

  const translationText = Array.isArray(translations_pt)
    ? translations_pt.join(", ")
    : translations_pt || "—";

  const handlePlayAudio = (e) => {
    e.stopPropagation();
    const audioElement = new Audio(audio);
    audioElement.play();
  };

  const handleClick = () => setShowTranslation((prev) => !prev);

  const handleAnswer = (status, difficulty) => {
    // Trigger the animation
    
    if (difficulty === "hard") playSound("/sounds/flashcardhard.mp3");
    else if (difficulty === "medium") playSound("/sounds/flashcardmedium.mp3");
    else if (difficulty === "easy") playSound("/sounds/flashcardeasy.mp3");

    const nextStatus = status; // "learning" or "known" in your current calls
    setUiStatus(nextStatus);
    setAnimateBorder(nextStatus);
  
    // Wait for animation to finish (1.6s) before actually moving to next word
    setTimeout(() => {
      if (trainingMode) {
        // 🚫 Training mode → no SRS update, no difficulty, no status
        onStatusChange(card, null, null);
      } else {
        // ✔ Normal review mode → apply SRS update
        onStatusChange(card, status, difficulty);
      }
      setShowTranslation(false);
      setAnimateBorder(null);
    }, 1600); // ← animation length + 1s hold
  };

  const formatPos = (pos) => {
    if (!pos) return "";
    const mapping = {
      adjective: "adj.",
      adv: "adv.",
      conj: "conj.",
      noun: "n.",
      particle: "part.",
      prep: "prep.",
      pron: "pron.",
      verb: "v.",
    };
    return mapping[pos.toLowerCase()] || `${pos[0].toLowerCase()}.`;
  };

  // --- Format word display ---
  const showPlural = pos?.toLowerCase() === "noun" && plural;
  const wordDisplay = (
    <>
      <div>
        {article ? `${article} ` : ""}
        {word}
      </div>
      {showPlural && (
        <div className="flashcard-plural">
          <span className="flashcard-pl-label">(pl.</span>{" "}
          {plural}
          <span className="flashcard-pl-label">)</span>
        </div>
      )}
    </>
  );
  

  return (
    <div className={`flashcard-item-modern 
        ${animateBorder ? `flashcard-${uiStatus}-animate` : ""} 
        ${uiStatus === "new" ? "flashcard-new" : ""} 
        ${uiStatus === "learning" ? "flashcard-learning" : ""} 
        ${uiStatus === "known" ? "flashcard-known" : ""}`}
      onClick={handleClick}
    >

      {!trainingMode && uiStatus === "new" && <div className="flashcard-badge badge-new">Novo</div>}
      {!trainingMode && uiStatus === "learning" && <div className="flashcard-badge badge-learning">Treino</div>}
      {!trainingMode && uiStatus === "known" && <div className="flashcard-badge badge-known">Já sei</div>}


      {/* === Top Content (German) === */}
      <div className="flashcard-top">
        {/* === Flag Button (Report) === */}
        <button
          className="flashcard-flag-btn"
          title="Reportar problema"
          onClick={(e) => {
            e.stopPropagation();
            setShowReport(true);
          }}
        >
          <IoFlagOutline size={22} />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
          <h2 className="flashcard-word">
            {pos && <span className="flashcard-pos-inline">{formatPos(pos)} </span>}
            {article ? `${article} ` : ""}
            {word}
          </h2>
          {audio && (
            <button
              className="flashcard-audio-btn"
              onClick={handlePlayAudio}
              title="Play pronunciation"
            >
              <HiOutlineSpeakerWave size={20} />
            </button>
          )}
        </div>

        {showPlural && (
          <div className="flashcard-plural">
            <span className="flashcard-pl-label">(pl.</span>{" "}
            {plural}
            <span className="flashcard-pl-label">)</span>
          </div>
        )}


        {/* Audio icon centered below word
        {audio && (
          <button
            className="flashcard-audio-btn centered"
            onClick={handlePlayAudio}
            title="Play pronunciation"
          >
            <HiOutlineSpeakerWave size={24} />
          </button>
        )} */}

        {/* Example sentence */}
        {sentence && !showTranslation && (<p className="flashcard-sentence">{sentence}</p>)}

        {showTranslation && sentenceTranslation && (
          <p className="flashcard-sentence-pt">{sentenceTranslation}</p>
        )}

      </div>

      {/* Divider */}
      <div className="flashcard-divider" />

      {/* === Bottom Content (Portuguese) === */}
      <div className="flashcard-bottom">
        {!showTranslation ? (
          <p className="flashcard-hint">Clique para traduzir</p>
        ) : (
          <>
            <p className="flashcard-translation">{translationText}</p>

            <div className="review-section">
              <div className="review-buttons">
                <button onClick={() => handleAnswer("learning", "hard")}>
                  Difícil
                  {!trainingMode && (
                    <span className="review-time">{previewNextLabel(card, "hard")}</span>
                  )}
                </button>

                <button onClick={() => handleAnswer("learning", "medium")}>
                  Médio
                  {!trainingMode && (
                    <span className="review-time">{previewNextLabel(card, "medium")}</span>
                  )}
                </button>

                <button onClick={() => handleAnswer("known", "easy")}>
                  Fácil
                  {!trainingMode && (
                    <span className="review-time">{previewNextLabel(card, "easy")}</span>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {showReport && (
        <ReportPopup
          onClose={() => setShowReport(false)}
          onSubmit={(data) => handleReportSubmit(data)}
        />
      )}


    </div>
  );
}
